import { useMemo } from 'react';
import { produce, type Draft } from 'immer';
import {
  calcIncomeStreamProjectedCoverage,
  calcSelfFundingProjectedCostCoverage,
  calculateMonthsBetweenYears,
  getCalcConfig,
} from '@shared';

export interface UseSelfFundingProjectedValueOptions {
  ltcAtYear: number;
  yearsTillLtc: number;
  /** Months of care used to include monthly income in projected value (from client.allPhaseCosts). */
  allPhaseCareMonthsNeeded?: number;
  //using it when available for the "age at LTC" label
  ltcAtAge?: number | null;
  //Fallback when ltcAtAge is not available: compute age from birth date and ltcAtYear.
  clientBirthDate?: string | Date | null;
}

/**
 * Computes projected value and age-at-LTC for a self-funding state using the same
 * calculation as the server (calcSelfFundingProjectedCostCoverage). Use for live
 * preview in SelfFundingDetailsModal and AccountCard.
 */
export function useSelfFundingProjectedValue(
  state: Partial<SelfFundingSource> | null | undefined,
  options: UseSelfFundingProjectedValueOptions,
): { projectedValue: number | null; ageAtLtc: number | null } {
  const {
    ltcAtYear,
    yearsTillLtc,
    allPhaseCareMonthsNeeded = 0,
    ltcAtAge: ltcAtAgeFromInference,
    clientBirthDate,
  } = options;

  return useMemo(() => {
    if (!state) {
      return { projectedValue: null, ageAtLtc: null };
    }

    const existingAssetsValue = state.existingAssetsValue;
    const annualRateOfReturn = state.annualRateOfReturn;
    const gainsTaxRate = state.gainsTaxRate;

    const { calcMethod } = getCalcConfig(state.accountType);
    const isIncomeStream = calcMethod === 'income-stream';

    const canCompute = isIncomeStream
      ? existingAssetsValue != null && gainsTaxRate != null
      : existingAssetsValue != null && annualRateOfReturn != null && gainsTaxRate != null;

    let projectedValue: number | null = null;
    if (canCompute) {
      const contributionStartYear = new Date().getFullYear();
      const contributionEndYear = ltcAtYear;
      const contributionMonthCount =
        calculateMonthsBetweenYears(contributionStartYear, contributionEndYear) || 1;

      const selfFundingSource: SelfFundingSource = {
        hasSelfFunding: true,
        existingAssetsValue: existingAssetsValue ?? null,
        monthlyContributionAmount: state.monthlyContributionAmount ?? null,
        annualRateOfReturn: annualRateOfReturn ?? null,
        gainsTaxRate: gainsTaxRate ?? null,
        contributionStartYear,
        contributionEndYear,
        contributionMonthCount,
        monthlyIncome: state.monthlyIncome ?? null,
        monthlyIncomeTaxRate: state.monthlyIncomeTaxRate ?? null,
        accountType: state.accountType ?? null,
        contributionStopYear: state.contributionStopYear ?? undefined,
        hasDrawdown: state.hasDrawdown ?? false,
        drawdownStartAtYear: state.drawdownStartAtYear ?? undefined,
        drawdownEndAtYear: state.drawdownEndAtYear ?? undefined,
        drawdownAmount: state.drawdownAmount ?? undefined,
        drawdownAmountInterval: state.drawdownAmountInterval ?? undefined,
        incomeReferenceAge: state.incomeReferenceAge ?? null,
      };

      const partialClient = {
        fundingSources: { selfFunding: selfFundingSource },
        allPhaseCosts: { allPhaseCareMonthsNeeded },
        appliedInferenceSet: {
          yearsTillLtc: Math.max(yearsTillLtc, 0),
          ltcAtYear,
          ltcAtAge: ltcAtAgeFromInference ?? undefined,
        },
      } as Partial<Client>;

      const calcFn = isIncomeStream
        ? calcIncomeStreamProjectedCoverage
        : calcSelfFundingProjectedCostCoverage;

      const result = produce(partialClient, (draft: Draft<Client>) => {
        calcFn(draft);
      }) as Partial<Client>;

      projectedValue = result.fundingSources?.selfFunding?.selfFundingTotalValue ?? null;
    }

    let ageAtLtc: number | null = null;
    if (ltcAtAgeFromInference != null && Number.isFinite(ltcAtAgeFromInference)) {
      ageAtLtc = ltcAtAgeFromInference;
    } else if (clientBirthDate) {
      try {
        const birth =
          typeof clientBirthDate === 'string' ? new Date(clientBirthDate) : clientBirthDate;
        const birthYear = birth.getFullYear();
        if (!Number.isNaN(birthYear)) {
          ageAtLtc = ltcAtYear - birthYear;
        }
      } catch {
        // leave ageAtLtc null
      }
    }

    return { projectedValue, ageAtLtc };
  }, [
    state?.existingAssetsValue,
    state?.monthlyContributionAmount,
    state?.annualRateOfReturn,
    state?.gainsTaxRate,
    state?.monthlyIncome,
    state?.monthlyIncomeTaxRate,
    state?.accountType,
    state?.contributionStopYear,
    state?.hasDrawdown,
    state?.drawdownStartAtYear,
    state?.drawdownEndAtYear,
    state?.drawdownAmount,
    state?.drawdownAmountInterval,
    state?.incomeReferenceAge,
    ltcAtYear,
    yearsTillLtc,
    allPhaseCareMonthsNeeded,
    ltcAtAgeFromInference,
    clientBirthDate,
  ]);
}
